import React, { useEffect, useState } from "react";
import axios from "axios";
import { Droplets } from "lucide-react";
import { API } from "../App";

/**
 * Complimentary car wash — the customer-facing surfaces.
 *
 * ONE source of truth: GET /api/promotions/wash-perk/active. It returns
 * only service ids, customer text and end date (never the cost, the VAT
 * rate or the rule name), and a paused / ended / not-yet-started rule is
 * simply absent — so every badge and banner below disappears on the next
 * fetch without any client-side status logic.
 */
export const useWashPerk = () => {
  const [perk, setPerk] = useState({ rules: [], covered_service_ids: [] });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/promotions/wash-perk/active`)
      .then((r) => { if (alive) setPerk(r.data || { rules: [], covered_service_ids: [] }); })
      .catch(() => { if (alive) setPerk({ rules: [], covered_service_ids: [] }); });
    return () => { alive = false; };
  }, []);

  const covered = new Set(perk.covered_service_ids || []);
  const ruleFor = (serviceId) =>
    (perk.rules || []).find((r) => (r.service_ids || []).includes(serviceId)) || null;

  return {
    rules: perk.rules || [],
    isCovered: (serviceId) => covered.has(serviceId),
    anyCovered: (serviceIds) => (serviceIds || []).some((id) => covered.has(id)),
    ruleFor,
    coveredIds: perk.covered_service_ids || [],
    textFor: (serviceId) => ruleFor(serviceId)?.customer_text || "Free car wash included",
    endsOn: (serviceId) => ruleFor(serviceId)?.end_date || null,
  };
};

/** Badge on a service card. Shows the end date only when the rule has one. */
export const WashPerkBadge = ({ endDate, testId = "wash-perk-badge" }) => (
  <span
    className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 text-xs rounded font-rajdhani inline-flex items-center gap-1"
    data-testid={testId}
  >
    <Droplets className="w-3 h-3" />
    FREE CAR WASH
    {endDate && <span className="text-cyan-400/70">· until {endDate}</span>}
  </span>
);

/** Staff-facing badge on the Job Board card and Job Card. Never a cost. */
export const WashIncludedBadge = ({ testId = "wash-included-badge" }) => (
  <span
    className="px-2 py-0.5 bg-cyan-500/15 text-cyan-300 text-[10px] uppercase tracking-wide rounded inline-flex items-center gap-1"
    data-testid={testId}
  >
    <Droplets className="w-3 h-3" /> Wash included
  </span>
);
